import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ─── Types ─────────────────────────────────────────────────────────────────────

export type SportDiscipline = 'running' | 'swimming' | 'cycling';
export type EntryType = 'record' | 'test';

export interface PersonalRecordPayload {
    type: 'record';
    sport: SportDiscipline;
    distance: string;
    timeValue: string;
    achievedAt: string;
    notes: string;
}

export interface PerformanceTestPayload {
    type: 'test';
    sport: SportDiscipline;
    testName: string;
    result: string;
    performedAt: string;
    notes: string;
}

export type EntryPayload = PersonalRecordPayload | PerformanceTestPayload;

export interface RecordInitialValues {
    id: string;
    distance: string;
    timeValue: string;
    achievedAt: string;
    notes: string;
}

export interface TestInitialValues {
    id: string;
    testName: string;
    result: string;
    performedAt: string;
    notes: string;
}

interface AddEntryModalProps {
    sport: SportDiscipline;
    entryType: EntryType;
    onSubmit: (payload: EntryPayload) => Promise<void> | void;
    onClose: () => void;
    /** When provided, the modal is in edit mode and pre-fills all fields. */
    initialValues?: RecordInitialValues | TestInitialValues;
}

// ─── Discipline Config ──────────────────────────────────────────────────────────

const SPORT_CONFIG: Record<SportDiscipline, { label: string; labelIt: string; color: string; icon: string; distances: string[] }> = {
    running: {
        label: 'Running',
        labelIt: 'Corsa',
        color: 'orange',
        icon: 'fa-person-running',
        distances: ['5 km', '10 km', '21 km', '42 km'],
    },
    swimming: {
        label: 'Swimming',
        labelIt: 'Nuoto',
        color: 'cyan',
        icon: 'fa-person-swimming',
        distances: ['100m', '200m', '400m', '800m', '1500m', '1900m', '2000m', '3800m', '5000m'],
    },
    cycling: {
        label: 'Cycling',
        labelIt: 'Ciclismo',
        color: 'yellow',
        icon: 'fa-person-biking',
        distances: ['20 km', '40 km', '90 km', '180 km'],
    },
};

const COLOR_CLASSES: Record<string, { ring: string; btn: string; badge: string; header: string }> = {
    orange: {
        ring: 'focus:ring-orange-400 focus:border-orange-400',
        btn: 'bg-orange-500 hover:bg-orange-600 text-white',
        badge: 'bg-orange-100 text-orange-700',
        header: 'from-orange-400',
    },
    cyan: {
        ring: 'focus:ring-cyan-400 focus:border-cyan-400',
        btn: 'bg-cyan-500 hover:bg-cyan-600 text-white',
        badge: 'bg-cyan-100 text-cyan-700',
        header: 'from-cyan-400',
    },
    yellow: {
        ring: 'focus:ring-yellow-400 focus:border-yellow-400',
        btn: 'bg-yellow-500 hover:bg-yellow-600 text-white',
        badge: 'bg-yellow-100 text-yellow-700',
        header: 'from-yellow-400',
    },
};

// ─── Time auto-formatter ──────────────────────────────────────────────────────
// Strips non-digits, keeps max 6, inserts colons → HH:MM:SS

const formatTime = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4)}`;
};

// ─── Field Wrapper ────────────────────────────────────────────────────────────

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({
    label,
    required,
    children,
}) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
        </label>
        {children}
    </div>
);

const inputCls = (ring: string) =>
    `w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 outline-none focus:ring-2 ${ring} transition-all`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const AddEntryModal: React.FC<AddEntryModalProps> = ({ sport, entryType, onSubmit, onClose, initialValues }) => {
    const { t } = useTranslation();
    const config = SPORT_CONFIG[sport];
    const colors = COLOR_CLASSES[config.color];
    const isEditing = Boolean(initialValues);

    // ── Derive initial record values ──
    const initRec = entryType === 'record' ? (initialValues as RecordInitialValues | undefined) : undefined;
    const initTest = entryType === 'test' ? (initialValues as TestInitialValues | undefined) : undefined;

    // ── Shared state ──
    const [notes, setNotes] = useState(initialValues?.notes ?? '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // ── Record-specific state ──
    const resolveDistance = () => {
        if (!initRec?.distance) return config.distances[0];
        return config.distances.includes(initRec.distance) ? initRec.distance : '__custom__';
    };
    const [selectedDistance, setSelectedDistance] = useState(resolveDistance);
    const [customDistance, setCustomDistance] = useState(
        initRec && !config.distances.includes(initRec.distance) ? initRec.distance : ''
    );
    const [isCustomDistance, setIsCustomDistance] = useState(
        Boolean(initRec && !config.distances.includes(initRec.distance))
    );
    const [timeValue, setTimeValue] = useState(initRec?.timeValue ?? '');
    const [achievedAt, setAchievedAt] = useState(initRec?.achievedAt ?? '');

    // ── Test-specific state ──
    const [testName, setTestName] = useState(initTest?.testName ?? '');
    const [result, setResult] = useState(initTest?.result ?? '');
    const [performedAt, setPerformedAt] = useState(initTest?.performedAt ?? '');

    const backdropRef = useRef<HTMLDivElement>(null);

    // Close on Escape key
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    const handleDistanceChange = (val: string) => {
        if (val === '__custom__') {
            setIsCustomDistance(true);
            setSelectedDistance('__custom__');
        } else {
            setIsCustomDistance(false);
            setSelectedDistance(val);
        }
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === backdropRef.current) onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (entryType === 'record') {
            const distance = isCustomDistance ? customDistance.trim() : selectedDistance;
            if (!distance) { setError(t('personalTest.validationDistance')); return; }
            if (!timeValue.trim()) { setError(t('personalTest.validationTime')); return; }
            if (!achievedAt) { setError(t('personalTest.validationDate')); return; }

            setLoading(true);
            try {
                await onSubmit({
                    type: 'record',
                    sport,
                    distance,
                    timeValue: timeValue.trim(),
                    achievedAt,
                    notes: notes.trim(),
                });
                onClose();
            } catch (err: any) {
                setError(err?.response?.data?.message || t('personalTest.validationError'));
            } finally {
                setLoading(false);
            }
        } else {
            if (!testName.trim()) { setError(t('personalTest.validationTestName')); return; }
            if (!result.trim()) { setError(t('personalTest.validationResult')); return; }
            if (!performedAt) { setError(t('personalTest.validationDate')); return; }

            setLoading(true);
            try {
                await onSubmit({
                    type: 'test',
                    sport,
                    testName: testName.trim(),
                    result: result.trim(),
                    performedAt,
                    notes: notes.trim(),
                });
                onClose();
            } catch (err: any) {
                setError(err?.response?.data?.message || t('personalTest.validationError'));
            } finally {
                setLoading(false);
            }
        }
    };

    const sportLabel = t(`personalTest.disciplines.${sport}`);
    const modalTitle = isEditing
        ? (entryType === 'record' ? `${t('personalTest.modalEditRecord')} — ${sportLabel}` : `${t('personalTest.modalEditTest')} — ${sportLabel}`)
        : (entryType === 'record' ? `${t('personalTest.modalNewRecord')} — ${sportLabel}` : `${t('personalTest.modalNewTest')} — ${sportLabel}`);

    return (
        <div
            ref={backdropRef}
            onClick={handleBackdropClick}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 animate-fade-in-down"
        >
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90dvh]">

                {/* Header */}
                <div className={`bg-gradient-to-r ${colors.header} to-white/0 px-6 py-4 flex items-center justify-between border-b border-slate-100 flex-shrink-0`}>
                    <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg ${colors.btn} flex items-center justify-center shadow-sm`}>
                            <i className={`fa-solid ${config.icon} text-sm`}></i>
                        </div>
                        <div>
                            <p className="font-bold text-slate-800 text-sm leading-tight">{modalTitle}</p>
                            <p className="text-xs text-slate-500">
                                {isEditing ? t('personalTest.modalSubtitleEdit') : (entryType === 'record' ? t('personalTest.modalSubtitleRecord') : t('personalTest.modalSubtitleTest'))}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
                    >
                        <i className="fa-solid fa-xmark text-base"></i>
                    </button>
                </div>

                {/* Discipline badge */}
                <div className="px-6 pt-4 flex-shrink-0">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${colors.badge}`}>
                        <i className={`fa-solid ${config.icon}`}></i>
                        {sportLabel}
                    </span>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-4 md:px-6 py-4 flex flex-col gap-3 md:gap-4 overflow-y-auto">

                    {entryType === 'record' ? (
                        <>
                            {/* Distance */}
                            <Field label={t('personalTest.modalDistanceLabel')} required>
                                <select
                                    value={selectedDistance}
                                    onChange={(e) => handleDistanceChange(e.target.value)}
                                    className={inputCls(colors.ring)}
                                >
                                    {config.distances.map((d) => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                    <option value="__custom__">{t('personalTest.modalOtherDistance')}</option>
                                </select>
                                {isCustomDistance && (
                                    <input
                                        type="text"
                                        placeholder={t('personalTest.modalDistancePlaceholder')}
                                        value={customDistance}
                                        onChange={(e) => setCustomDistance(e.target.value)}
                                        className={`${inputCls(colors.ring)} mt-2`}
                                        autoFocus
                                    />
                                )}
                            </Field>

                            {/* Tempo + Data — side by side */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label={t('personalTest.modalTimeLabel')} required>
                                    <input
                                        type="text"
                                        placeholder={t('personalTest.modalTimePlaceholder')}
                                        value={timeValue}
                                        onChange={(e) => setTimeValue(formatTime(e.target.value))}
                                        className={inputCls(colors.ring)}
                                        maxLength={8}
                                        inputMode="numeric"
                                    />
                                </Field>
                                <Field label={t('common.date')} required>
                                    <input
                                        type="date"
                                        value={achievedAt}
                                        onChange={(e) => setAchievedAt(e.target.value)}
                                        className={inputCls(colors.ring)}
                                        max={new Date().toISOString().split('T')[0]}
                                    />
                                </Field>
                            </div>

                            {/* Note */}
                            <Field label={t('personalTest.modalNotesLabel')}>
                                <input
                                    type="text"
                                    placeholder={t('personalTest.modalNotesPlaceholder')}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className={inputCls(colors.ring)}
                                />
                            </Field>
                        </>
                    ) : (
                        <>
                            {/* Test name */}
                            <Field label={t('personalTest.modalTestNameLabel')} required>
                                <input
                                    type="text"
                                    placeholder={t('personalTest.modalTestNamePlaceholder')}
                                    value={testName}
                                    onChange={(e) => setTestName(e.target.value)}
                                    className={inputCls(colors.ring)}
                                    autoFocus
                                />
                            </Field>

                            {/* Risultato + Data — side by side */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label={t('personalTest.modalResultLabel')} required>
                                    <input
                                        type="text"
                                        placeholder={t('personalTest.modalResultPlaceholder')}
                                        value={result}
                                        onChange={(e) => setResult(e.target.value)}
                                        className={inputCls(colors.ring)}
                                    />
                                </Field>
                                <Field label={t('common.date')} required>
                                    <input
                                        type="date"
                                        value={performedAt}
                                        onChange={(e) => setPerformedAt(e.target.value)}
                                        className={inputCls(colors.ring)}
                                        max={new Date().toISOString().split('T')[0]}
                                    />
                                </Field>
                            </div>

                            {/* Note */}
                            <Field label={t('personalTest.modalNotesLabelTest')}>
                                <input
                                    type="text"
                                    placeholder={t('personalTest.modalNoteTestPlaceholder')}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className={inputCls(colors.ring)}
                                />
                            </Field>
                        </>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                            <i className="fa-solid fa-circle-exclamation"></i>
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2 rounded-lg text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
                            disabled={loading}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-5 py-2 rounded-lg text-sm font-semibold ${colors.btn} transition-colors flex items-center gap-2 disabled:opacity-60`}
                        >
                            {loading ? (
                                <><i className="fa-solid fa-spinner fa-spin"></i> {t('common.saving')}</>
                            ) : isEditing ? (
                                <><i className="fa-solid fa-pen"></i> {t('common.update')}</>
                            ) : (
                                <><i className="fa-solid fa-floppy-disk"></i> {t('common.save')}</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddEntryModal;
